import { RxCollection, RxDocument } from 'rxdb';

import { User } from 'types/user';
import { Survey } from 'types/survey';
import { SurveyResponse } from 'types/surveyResponse';
import { CustomerVisit, CustomerVisitDb } from 'types/customer';
import { Product } from 'types/product';
import { ModifiedDate } from 'types/date';
import { PendingBlob } from 'types/pendingBlob';
import {
  CustomerVisitsMethods,
  Database,
  ModifiedDatesMethods,
  PendingBlobsMethods,
  ProductsMethods,
  SubmittedSurveysMethods,
  UnsubmittedSurveysMethods,
  UserMethods
} from './types';

const incrementalUpsert = async <T>(collection: RxCollection<T>, values: T | T[]) => {
  if (Array.isArray(values)) {
    return await Promise.all(values.map((value) => collection.incrementalUpsert(value)));
  }

  return await collection.incrementalUpsert(values);
};

export const userMethods = (database: Database): UserMethods => {
  return {
    upsertUser: async function (user: User) {
      const collection = database.user as unknown as RxCollection<User>;
      await collection.upsert(user);
    },
    getUser: async function () {
      const doc: RxDocument<User> | null = database.user ? await database.user.findOne().exec() : null;
      return doc ? ((doc as RxDocument<User>).toJSON() as User) : null;
    },
    deleteUser: async function (userId: string) {
      const collection = database.user as unknown as RxCollection<User>;
      const doc: RxDocument<User> | null = database.user
        ? ((await collection.findOne(userId).exec()) as RxDocument<User>)
        : null;

      if (doc) {
        await doc.remove();

        return true;
      }

      return false;
    },
    purgeUsers: async function () {
      const collection = database.user as unknown as RxCollection<User>;
      const docs: RxDocument<User>[] | null = collection ? await collection.find().exec() : null;

      if (docs) {
        await Promise.all(docs.map((doc) => doc.remove()));
      }
    }
  } as UserMethods;
};

export const unsubmittedSurveysMethods = (database: Database): UnsubmittedSurveysMethods => {
  return {
    upsertSurveys: async function (surveys: Survey[]) {
      const collection = database.unsubmittedSurveys as unknown as RxCollection<Survey>;
      const result = await collection.bulkInsert(surveys);
      let success = result.success?.length === surveys.length;
      const hasErrors = result.error?.length > 0;

      if (hasErrors && result.error.find((error) => error?.isError === true && error?.status === 409)) {
        console.warn('Duplicate survey definitions found, running incremental upsert...');
        await incrementalUpsert<Survey>(collection, surveys);
        success = true;
      } else if (hasErrors) {
        console.error('Errors occurred during upsert of survey definitions:', result.error);
      }

      return success;
    },
    getSurveys: async function () {
      const docs: RxDocument<Survey>[] | null = database.unsubmittedSurveys
        ? await database.unsubmittedSurveys.find().exec()
        : null;

      return docs ? docs.map((doc) => doc.toJSON() as Survey) : [];
    },
    getSurveyByDefinitionId: async function (definitionId: string) {
      const doc: RxDocument<Survey> | null = database.unsubmittedSurveys
        ? await database.unsubmittedSurveys.findOne(definitionId).exec()
        : null;

      return doc ? ((doc as RxDocument<Survey>).toJSON() as Survey) : null;
    }
  } as UnsubmittedSurveysMethods;
};

export const submittedSurveysMethods = (database: Database): SubmittedSurveysMethods => {
  return {
    upsertSurveyResponses: async function (surveyResponses: SurveyResponse[]) {
      const collection = database.submittedSurveys as unknown as RxCollection<SurveyResponse>;
      const result = await collection.bulkInsert(surveyResponses);
      let success = result.success?.length === surveyResponses.length;
      const hasErrors = result.error?.length > 0;

      if (hasErrors && result.error.find((error) => error?.isError === true && error?.status === 409)) {
        console.warn('Duplicate survey responses found, running incremental upsert...');
        await incrementalUpsert<SurveyResponse>(collection, surveyResponses);
        success = true;
      } else if (hasErrors) {
        console.error('Errors occurred during upsert of survey responses:', result.error);
      }

      return success;
    },
    getSurveyResponses: async function () {
      const collection = database.submittedSurveys as unknown as RxCollection<SurveyResponse>;
      const docs: RxDocument<SurveyResponse>[] | null = collection ? await collection.find().exec() : null;

      return docs ? docs.map((doc) => doc.toJSON() as SurveyResponse) : [];
    },
    getSurveyResponseById: async function (responseId: string) {
      const collection = database.submittedSurveys as unknown as RxCollection<SurveyResponse>;
      const doc: RxDocument<SurveyResponse> | null = collection ? await collection.findOne(responseId).exec() : null;

      return doc ? (doc.toJSON() as SurveyResponse) : null;
    },
    deleteSurveyResponse: async function (responseId: string) {
      const collection = database.submittedSurveys as unknown as RxCollection<SurveyResponse>;
      const doc: RxDocument<SurveyResponse> | null = database.submittedSurveys
        ? ((await collection.findOne(responseId).exec()) as RxDocument<SurveyResponse>)
        : null;

      if (doc) {
        await doc.remove();

        return true;
      }

      return false;
    }
  } as SubmittedSurveysMethods;
};

export const customerVisitsMethods = (database: Database): CustomerVisitsMethods => {
  const methods = {
    upsertCustomerVisits: async function (customerVisits: CustomerVisit[]) {
      const collection = database.customerVisits as unknown as RxCollection<CustomerVisitDb>;
      const transformedData = [] as CustomerVisitDb[];

      customerVisits.forEach((visit) => {
        if (!visit.plannedVisit.owner.email) {
          // happens at least with some test data, wouldn't matter but empty key causes a crash in RxDB otherwise
          visit.plannedVisit.owner.email = 'email-missing@mirka.com';
        }

        const existing = transformedData.find((v) => v.email === visit.plannedVisit.owner.email);

        if (existing) {
          existing.customerVisits.push(visit);
        } else {
          transformedData.push({
            email: visit.plannedVisit.owner.email,
            customerVisits: [visit]
          });
        }
      });

      const result = await collection.bulkInsert(transformedData);
      let success = result.success?.length === transformedData.length;
      const hasErrors = result.error?.length > 0;

      if (hasErrors && result.error.find((error) => error?.isError === true && error?.status === 409)) {
        console.warn('Duplicate customer visits found, running incremental upsert...');
        await incrementalUpsert<CustomerVisitDb>(collection, transformedData);
        success = true;
      } else if (hasErrors) {
        console.error('Errors occurred during upsert of customer visits:', result.error);
      }

      return success;
    },
    getCustomerVisits: async function (email: string) {
      const collection = database.customerVisits as unknown as RxCollection<CustomerVisitDb>;
      const customerVisits = [] as CustomerVisit[];
      let docs: Map<string, RxDocument<CustomerVisitDb, {}>> | null = collection
        ? await collection.findByIds([email]).exec()
        : null;

      if (docs) {
        const dbVisits: CustomerVisitDb[] = [];
        docs.forEach((doc) => dbVisits.push(doc.toJSON() as CustomerVisitDb));
        dbVisits.forEach((dbVisit) => {
          customerVisits.push(...dbVisit.customerVisits);
        });
      }

      return customerVisits ?? [];
    }
  } as CustomerVisitsMethods;

  return methods;
};

export const productMethods = (database: Database) => {
  return {
    upsertProducts: async function (products: Product[]) {
      const collection = database.products as unknown as RxCollection<Product>;
      const result = await collection.bulkInsert(products);
      let success = result.success?.length === products.length;
      const hasErrors = result.error?.length > 0;

      if (hasErrors && result.error.find((error) => error?.isError === true && error?.status === 409)) {
        console.warn('Duplicate products found, running incremental upsert...');
        await incrementalUpsert<Product>(collection, products);
        success = true;
      } else if (hasErrors) {
        console.error('Errors occurred during upsert of products:', result.error);
      }

      return success;
    },
    getProducts: async function () {
      const collection = database.products as unknown as RxCollection<Product>;
      const docs: RxDocument<Product>[] | null = collection ? await collection.find().exec() : null;

      return docs ? docs.map((doc) => doc.toJSON() as Product) : [];
    },
    getProductById: async function (productId) {
      const collection = database.products as unknown as RxCollection<Product>;
      const doc: RxDocument<Product> | null = collection ? await collection.findOne(productId).exec() : null;

      return doc ? (doc.toJSON() as Product) : null;
    }
  } as ProductsMethods;
};

export const modifiedDatesMethods = (database: Database): ModifiedDatesMethods => {
  return {
    upsertModifiedDates: async function (modifiedDates: ModifiedDate[]) {
      const collection = database.modifiedDates as unknown as RxCollection<ModifiedDate>;
      const result = await collection.bulkInsert(modifiedDates);
      let success = result.success?.length === modifiedDates.length;
      const hasErrors = result.error?.length > 0;

      if (hasErrors && result.error.find((error) => error?.isError === true && error?.status === 409)) {
        console.warn('Duplicate modified dates found, running incremental upsert...');
        await incrementalUpsert<ModifiedDate>(collection, modifiedDates);
        success = true;
      } else if (hasErrors) {
        console.error('Errors occurred during upsert of modified dates:', result.error);
      }

      return success;
    }
  } as ModifiedDatesMethods;
};

export const pendingBlobMethods = (database: Database): PendingBlobsMethods => {
  return {
    getPendingBlobByName: async (name: string) => {
      const collection = database.pendingBlobs as unknown as RxCollection<PendingBlob>;
      return (await collection.findOne(name).exec())?.toJSON();
    },
    upsertBlob: async (pendingBlob: PendingBlob) => {
      const collection = database.pendingBlobs as unknown as RxCollection<PendingBlob>;
      return !!(await collection.upsert(pendingBlob));
    },
    deletePendingBlob: async (name: string) => {
      const collection = database.pendingBlobs as unknown as RxCollection<PendingBlob>;
      return !!(await collection.findOne(name).remove());
    }
  } as PendingBlobsMethods;
};
